import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6200'

export default function CreateTenancyFlow({ onBack }) {
  const { token, updateUser } = useAuth()
  const [step, setStep] = useState('type') // 'type' or 'details'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    tenancy_type: '',
    name: '',
    user_role: 'OWNER'
  })

  const handleTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, tenancy_type: type }))
    setStep('details')
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${API_URL}/api/v1/tenancies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create organization')
      }

      // Update user context with new active tenancy
      if (data.user) {
        updateUser(data.user)
      }
    } catch (err) {
      setError(err.message || 'Failed to create organization. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (step === 'type') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h3 className="text-[#111418] text-2xl font-bold mb-2">
            Create Your Organization
          </h3>
          <p className="text-[#617589] text-base">
            What type of organization do you want to create?
          </p>
        </div>

        {/* Type Selection Cards */}
        <div className="space-y-4">
          <button
            onClick={() => handleTypeSelect('CLIENT')}
            className="w-full p-6 border-2 border-[#dbe0e6] rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <span className="material-symbols-outlined text-blue-600 group-hover:text-primary text-2xl">business</span>
              </div>
              <div className="flex-1">
                <h4 className="text-[#111418] text-lg font-bold mb-1">Client</h4>
                <p className="text-[#617589] text-sm leading-relaxed">
                  I'm managing my own migrations and want to track my company's projects
                </p>
              </div>
              <span className="material-symbols-outlined text-[#617589] text-2xl">arrow_forward</span>
            </div>
          </button>

          <button
            onClick={() => handleTypeSelect('SERVICE_PROVIDER')}
            className="w-full p-6 border-2 border-[#dbe0e6] rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <span className="material-symbols-outlined text-purple-600 group-hover:text-primary text-2xl">groups</span>
              </div>
              <div className="flex-1">
                <h4 className="text-[#111418] text-lg font-bold mb-1">Service Provider</h4>
                <p className="text-[#617589] text-sm leading-relaxed">
                  I help clients with migrations (accounting, consulting, integration firm, etc.)
                </p>
              </div>
              <span className="material-symbols-outlined text-[#617589] text-2xl">arrow_forward</span>
            </div>
          </button>
        </div>

        {/* Back Button */}
        <button
          onClick={onBack}
          className="w-full text-[#617589] hover:text-[#111418] text-sm font-medium transition-colors py-2"
        >
          ← Back
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-[#111418] text-2xl font-bold mb-2">
          Organization Details
        </h3>
        <p className="text-[#617589] text-base">
          {formData.tenancy_type === 'CLIENT'
            ? 'Tell us about your company'
            : 'Tell us about your service organization'}
        </p>
      </div>

      {/* Type Badge */}
      <div className="flex items-center justify-center">
        <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
          formData.tenancy_type === 'CLIENT'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-purple-100 text-purple-700'
        }`}>
          <span className="material-symbols-outlined text-lg">
            {formData.tenancy_type === 'CLIENT' ? 'business' : 'groups'}
          </span>
          {formData.tenancy_type === 'CLIENT' ? 'Client Organization' : 'Service Provider'}
        </span>
      </div>

      {/* Organization Name */}
      <label className="flex flex-col">
        <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
          Organization Name <span className="text-red-500">*</span>
        </p>
        <input
          className="form-input flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white h-12 px-4 placeholder:text-[#617589] text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
          placeholder={formData.tenancy_type === 'CLIENT' ? 'ACME Corporation' : 'Smith & Co Accounting'}
          required
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
        />
      </label>

      {/* Your Role */}
      <label className="flex flex-col">
        <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
          Your Role <span className="text-red-500">*</span>
        </p>
        <select
          className="form-select flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white h-12 px-4 placeholder:text-[#617589] text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
          name="user_role"
          value={formData.user_role}
          onChange={handleChange}
          required
        >
          <option value="OWNER">Owner</option>
          <option value="ADMIN">Admin</option>
          <option value="MEMBER">Member</option>
        </select>
        <p className="text-xs text-[#617589] mt-1">
          You can change roles and invite team members later
        </p>
      </label>

      {/* Error Message */}
      {error && (
        <div className="rounded-lg bg-red-50 p-4 border border-red-200">
          <div className="flex items-start">
            <span className="material-symbols-outlined text-red-600 mr-3 text-[20px]">error</span>
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => setStep('type')}
          className="flex-1 border-2 border-[#dbe0e6] text-[#111418] font-bold rounded-lg h-12 hover:bg-gray-50 transition-all"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold rounded-lg h-12 shadow-md transition-all focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating...' : 'Create Organization'}
        </button>
      </div>
    </form>
  )
}
